/**
 * Centralized Access Control System for ParkProUI
 * 
 * This file defines all page-level access controls in one place,
 * making it easy to manage permissions across the application.
 */

// User roles hierarchy (from lowest to highest access)
export const USER_ROLES = {
  AGENT: 'agent',           // Solo agents
  AGENT_ADMIN: 'agentAdmin', // Agency owners/managers
  PARKPRO_ADMIN: 'parkProAdmin' // Internal ParkPro team
};

// Special users (hardcoded for specific features)
export const SPECIAL_USERS = {
  KEVIN: 'ktodoran'
};

/**
 * Check if user has required role or higher
 * @param {Object} user - User object
 * @param {string} requiredRole - Required role level
 * @returns {boolean}
 */
export const hasRole = (user, requiredRole) => {
  if (!user || !user.role) return false;
  
  const roleHierarchy = {
    [USER_ROLES.AGENT]: 1,
    [USER_ROLES.AGENT_ADMIN]: 2,
    [USER_ROLES.PARKPRO_ADMIN]: 3
  };
  
  const userLevel = roleHierarchy[user.role] || 0;
  const requiredLevel = roleHierarchy[requiredRole] || 0;
  
  return userLevel >= requiredLevel;
};

/**
 * Check if user is a special user (hardcoded access)
 * @param {Object} user - User object
 * @param {string} specialUser - Special user identifier
 * @returns {boolean}
 */
export const isSpecialUser = (user, specialUser) => {
  if (!user || !user.username) return false;
  return user.username === specialUser;
};

/**
 * Check if user has ParkPro Admin access (role or special user)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const hasParkProAdminAccess = (user) => {
  return hasRole(user, USER_ROLES.PARKPRO_ADMIN) || 
         isSpecialUser(user, SPECIAL_USERS.KEVIN);
};

/**
 * Check if user has Agency Admin access (role or higher)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const hasAgencyAdminAccess = (user) => {
  return hasRole(user, USER_ROLES.AGENT_ADMIN);
};

/**
 * Check if user has basic agent access (any authenticated user)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const hasAgentAccess = (user) => {
  return !!user;
};

// Page Access Control Definitions
export const PAGE_ACCESS = {
  // Core Agent Pages - Available to ALL authenticated users
  DASHBOARD: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Main dashboard for all users'
  },
  
  CLIENT_MANAGEMENT: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Client management for all users'
  },
  
  TRIP_MANAGEMENT: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Trip management for all users'
  },
  
  DISNEY_WORLD: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Disney World trip planning for all users'
  },
  
  COMMISSIONS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Commission tracking for all users'
  },
  
  LEADS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Lead management for all users'
  },
  
  COMMUNICATIONS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Communications hub for all users'
  },
  
  SETTINGS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'User settings for all users'
  },
  
  // Premium Destinations - ParkPro Admin ONLY
  DISNEYLAND: {
    roles: [USER_ROLES.PARKPRO_ADMIN],
    specialUsers: [SPECIAL_USERS.KEVIN],
    description: 'Disneyland trip planning - ParkPro Admin only'
  },
  
  DISNEY_CRUISE: {
    roles: [USER_ROLES.PARKPRO_ADMIN],
    specialUsers: [SPECIAL_USERS.KEVIN],
    description: 'Disney Cruise trip planning - ParkPro Admin only'
  },
  
  UNIVERSAL_ORLANDO: {
    roles: [USER_ROLES.PARKPRO_ADMIN],
    specialUsers: [SPECIAL_USERS.KEVIN],
    description: 'Universal Orlando trip planning - ParkPro Admin only'
  },
  
  // Agency Admin Pages - Agency Admin and ParkPro Admin
  AGENCY_DASHBOARD: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Agency dashboard - Agency Admin and ParkPro Admin only'
  },
  
  AGENT_MANAGEMENT: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Agent management - Agency Admin and ParkPro Admin only'
  },
  
  FINANCIAL_MANAGEMENT: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Financial management - Agency Admin and ParkPro Admin only'
  },
  
  WHITE_LABELING: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'White labeling - Agency Admin and ParkPro Admin only'
  },
  
  AGENCY_SETTINGS: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Agency settings - Agency Admin and ParkPro Admin only'
  },
  
  AGENCY_REPORTS: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Agency reports - Agency Admin and ParkPro Admin only'
  },
  
  // Sales Pipeline - ParkPro Admin ONLY
  SALES_PIPELINE: {
    roles: [USER_ROLES.PARKPRO_ADMIN],
    specialUsers: [SPECIAL_USERS.KEVIN],
    description: 'Sales pipeline - ParkPro Admin only'
  },
  
  // CRM Trip Management - All Users
  CRM_ADD_TRIP: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Add new trips - All authenticated users'
  },
  
  CRM_CLIENT_DETAILS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Client details - All authenticated users'
  },
  
  CRM_ADD_CLIENT: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Add new clients - All authenticated users'
  }
};

/**
 * Check if user has access to a specific page
 * @param {Object} user - User object
 * @param {string} pageKey - Page key from PAGE_ACCESS
 * @returns {boolean}
 */
export const hasPageAccess = (user, pageKey) => {
  const pageConfig = PAGE_ACCESS[pageKey];
  if (!pageConfig) {
    console.warn(`Page access config not found for: ${pageKey}`);
    return false;
  }
  
  // Check role-based access
  if (pageConfig.roles && pageConfig.roles.length > 0) {
    const hasRequiredRole = pageConfig.roles.some(role => hasRole(user, role));
    if (hasRequiredRole) return true;
  }
  
  // Check special user access
  if (pageConfig.specialUsers && pageConfig.specialUsers.length > 0) {
    const hasSpecialAccess = pageConfig.specialUsers.some(specialUser => 
      isSpecialUser(user, specialUser)
    );
    if (hasSpecialAccess) return true;
  }
  
  return false;
};

/**
 * Get user's accessible pages
 * @param {Object} user - User object
 * @returns {Array} Array of page keys user can access
 */
export const getAccessiblePages = (user) => {
  return Object.keys(PAGE_ACCESS).filter(pageKey => 
    hasPageAccess(user, pageKey)
  );
};

/**
 * Get page access info for debugging
 * @param {Object} user - User object
 * @param {string} pageKey - Page key from PAGE_ACCESS
 * @returns {Object} Access info object
 */
export const getPageAccessInfo = (user, pageKey) => {
  const pageConfig = PAGE_ACCESS[pageKey];
  const hasAccess = hasPageAccess(user, pageKey);
  
  return {
    pageKey,
    hasAccess,
    userRole: user?.role,
    username: user?.username,
    requiredRoles: pageConfig?.roles || [],
    specialUsers: pageConfig?.specialUsers || [],
    description: pageConfig?.description || 'No description available'
  };
};

// Legacy compatibility functions (for gradual migration)
export const hasCRMAccess = (user) => hasAgentAccess(user);
export const hasAgencyAdminAccessLegacy = (user) => hasAgencyAdminAccess(user);
export const hasNewDestinationAccess = (user) => hasParkProAdminAccess(user);
